import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  productsApi,
  sellersApi,
  schedulesApi,
  type Product,
  type ProductsListParams,
  type SellerListItem,
  type Schedule,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBasePath, withBase } from '../lib/basePath';

const PAGE_SIZE = 50;

/**
 * Inline editor for the Flowwow-required properties that must be filled in
 * per product before publishing. Lives under each row in ProductsList as a
 * quick-edit drawer. The full editor is still available on the product page.
 */
function QuickEditor({ product, onSave }: {
  product: Product;
  onSave: (patch: Parameters<typeof productsApi.update>[1]) => void | Promise<void>;
}) {
  const props = product.properties as Record<string, string | undefined>;
  const [height, setHeight] = useState(props.height?.toString() ?? '');
  const [width,  setWidth]  = useState(props.width?.toString()  ?? '');
  const [manufacturer, setManufacturer] = useState(props.manufacturerName ?? '');
  const [moq, setMoq] = useState(product.min_order_count?.toString() ?? '1');
  const [discount, setDiscount] = useState(product.discount_percent?.toString() ?? '');
  const [country, setCountry] = useState(product.manufacturer_country_id?.toString() ?? '1');
  const [vat, setVat] = useState(product.vat_id?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function save() {
    setBusy(true);
    const newProps: Record<string, string> = { ...(product.properties as Record<string, string>) };
    if (height.trim()) newProps.height = height.trim(); else delete newProps.height;
    if (width.trim())  newProps.width  = width.trim();  else delete newProps.width;
    if (manufacturer.trim()) newProps.manufacturerName = manufacturer.trim();
    else                     delete newProps.manufacturerName;
    try {
      await onSave({
        properties: newProps,
        min_order_count:         moq ? Number(moq) : null,
        discount_percent:        discount ? Number(discount) : null,
        manufacturer_country_id: country ? Number(country) : null,
        vat_id:                  vat ? Number(vat) : null,
      });
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  }

  const justSaved = savedAt && Date.now() - savedAt < 3000;

  return (
    <div className="quick-edit">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="qe-field" style={{ minWidth: 110 }}>
          <span>Высота, см</span>
          <input type="number" min={1} max={9999} value={height} onChange={(e) => setHeight(e.target.value)} />
        </label>
        <label className="qe-field" style={{ minWidth: 110 }}>
          <span>Ширина, см</span>
          <input type="number" min={1} max={9999} value={width} onChange={(e) => setWidth(e.target.value)} />
        </label>
        <label className="qe-field" style={{ minWidth: 110 }}>
          <span>Мин. заказ</span>
          <input type="number" min={1} value={moq} onChange={(e) => setMoq(e.target.value)} />
        </label>
        <label className="qe-field" style={{ minWidth: 110 }}>
          <span>Скидка, %</span>
          <input type="number" min={0} max={99} value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" />
        </label>
        <label className="qe-field" style={{ flex: 1, minWidth: 180 }}>
          <span>Производитель</span>
          <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="Florentika" />
        </label>
        <label className="qe-field" style={{ minWidth: 150 }}>
          <span>Страна</span>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">— не указано —</option>
            <option value="1">Россия</option>
            <option value="3">Беларусь</option>
            <option value="4">Казахстан</option>
            <option value="11">Кыргызстан</option>
            <option value="18">Узбекистан</option>
          </select>
        </label>
        <label className="qe-field" style={{ minWidth: 110 }}>
          <span>НДС</span>
          <select value={vat} onChange={(e) => setVat(e.target.value)}>
            <option value="">—</option>
            <option value="10">10%</option>
            <option value="22">22%</option>
          </select>
        </label>
        <button className="btn primary" disabled={busy} onClick={save}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
        {justSaved && <span className="ok-msg">✓ Сохранено</span>}
      </div>
    </div>
  );
}

export default function ProductsList() {
  const { user } = useAuth();
  const base = useBasePath();

  const [filterSeller, setFilterSeller]     = useState<number | ''>('');
  const [filterSchedule, setFilterSchedule] = useState<number | ''>('');
  const [filterActive, setFilterActive]     = useState<'' | 'on' | 'off'>('');
  const [q, setQ]                           = useState('');
  const [offset, setOffset]                 = useState(0);

  const [sellers, setSellers]     = useState<SellerListItem[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  // Photo upload uses a per-row native <input type="file"> via <label>,
  // so the browser dispatches the file dialog directly with no JS handoff.
  // The earlier shared-input + state approach was racy under React 18's
  // automatic batching, so we drop it.
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  async function onPickFiles(productId: number, files: FileList | null) {
    if (!files || !files.length) return;
    setUploadingId(productId);
    try {
      const r = await productsApi.uploadImages(productId, Array.from(files));
      setItems((prev) => prev.map((p) => p.id === productId ? { ...p, images: r.images } : p));
    } catch (e) {
      alert(String((e as Error)?.message ?? e));
    } finally {
      setUploadingId(null);
    }
  }

  // Inline-editable Flowwow properties per row. Toggling expands a quick-edit
  // panel under the row with all required fields; saves via PUT on Save.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  async function savePatch(id: number, patch: Parameters<typeof productsApi.update>[1]) {
    try {
      const r = await productsApi.update(id, patch);
      setItems((prev) => prev.map((p) => p.id === id ? r.product : p));
    } catch (e) {
      alert(String((e as Error)?.message ?? e));
    }
  }

  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
    }
  }, [user]);

  useEffect(() => {
    schedulesApi.list(typeof filterSeller === 'number' ? filterSeller : undefined)
      .then((r) => setSchedules(r.schedules))
      .catch(() => setSchedules([]));
  }, [filterSeller]);

  useEffect(() => {
    setLoading(true);
    const params: ProductsListParams = { limit: PAGE_SIZE, offset };
    if (filterSeller !== '')   params.seller_id = filterSeller;
    if (filterSchedule !== '') params.schedule_group_id = filterSchedule;
    if (filterActive === 'on') params.is_active = true;
    if (filterActive === 'off') params.is_active = false;
    if (q.trim())              params.q = q.trim();
    productsApi.list(params)
      .then((r) => { setItems(r.products); setTotal(r.total); setErr(null); })
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [filterSeller, filterSchedule, filterActive, q, offset]);

  function sellerLabel(id: number) {
    const s = sellers.find((x) => x.id === id);
    return s ? (s.display_name || s.login) : `#${id}`;
  }

  function scheduleLabel(id: number | null) {
    if (id == null) return <span className="muted">—</span>;
    const s = schedules.find((x) => x.id === id);
    return s ? s.name : `#${id}`;
  }

  return (
    <div className="container">
      <div className="page-head">
        <h2>Товары</h2>
        <Link to={withBase(base, '/products/new')} className="btn primary">+ Новый товар</Link>
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 16, alignItems: 'flex-end' }}>
        {user?.role === 'admin' && (
          <label>
            <div className="muted" style={{ fontSize: 12 }}>Продавец</div>
            <select
              value={filterSeller}
              onChange={(e) => { setOffset(0); setFilterSeller(e.target.value ? Number(e.target.value) : ''); }}
            >
              <option value="">Все</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.login}</option>
              ))}
            </select>
          </label>
        )}
        <label>
          <div className="muted" style={{ fontSize: 12 }}>Расписание</div>
          <select
            value={filterSchedule}
            onChange={(e) => { setOffset(0); setFilterSchedule(e.target.value ? Number(e.target.value) : ''); }}
          >
            <option value="">Все</option>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label>
          <div className="muted" style={{ fontSize: 12 }}>Активность</div>
          <select
            value={filterActive}
            onChange={(e) => { setOffset(0); setFilterActive(e.target.value as '' | 'on' | 'off'); }}
          >
            <option value="">Все</option>
            <option value="on">Активные</option>
            <option value="off">Скрытые</option>
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 200 }}>
          <div className="muted" style={{ fontSize: 12 }}>Поиск (имя / артикул)</div>
          <input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="например: Абигейл / Fla00035c"
          />
        </label>
      </div>

      {err && <div className="error">{err}</div>}
      {loading && <p className="muted">Загрузка…</p>}

      {!loading && items.length === 0 ? (
        <p className="muted">Ничего не найдено.</p>
      ) : (
        <>
          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th style={{ width: 60 }}>Фото</th>
                <th>Артикул</th>
                <th>Название</th>
                {user?.role === 'admin' && <th>Продавец</th>}
                <th>Цена ₽</th>
                <th>Расписание</th>
                <th>Окно</th>
                <th>Активен</th>
                <th>Последний push</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const isOpen = expandedId === p.id;
                const colSpan = 9 + (user?.role === 'admin' ? 1 : 0);
                return (
                  <Fragment key={p.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => setExpandedId(isOpen ? null : p.id)}
                          title="Свойства Flowwow"
                        >{isOpen ? '▾' : '▸'}</button>
                      </td>
                      <td>
                        <div className="thumb-cell">
                          {p.images[0]
                            ? <img src={p.images[0]} alt="" className="thumb" />
                            : <span className="thumb empty" />}
                          <label
                            className="thumb-upload"
                            htmlFor={`upload-${p.id}`}
                            title="Загрузить фото"
                          >{uploadingId === p.id ? '…' : (p.images.length > 0 ? `+${p.images.length}` : '+')}</label>
                          <input
                            id={`upload-${p.id}`}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                            disabled={uploadingId === p.id}
                            onChange={(e) => {
                              const fs = e.target.files;
                              e.target.value = '';
                              onPickFiles(p.id, fs);
                            }}
                          />
                        </div>
                      </td>
                      <td><code>{p.external_offer_id}</code></td>
                      <td><Link to={withBase(base, `/products/${p.id}`)}>{p.name}</Link></td>
                      {user?.role === 'admin' && <td>{sellerLabel(p.seller_id)}</td>}
                      <td>{p.price_rub.toLocaleString('ru-RU')}</td>
                      <td>{scheduleLabel(p.schedule_group_id)}</td>
                      <td>
                        <span className={`badge ${p.current_window_state === 'open' ? 'ok' : 'warn'}`}>
                          {p.current_window_state}
                        </span>
                      </td>
                      <td>{p.is_active ? '✓' : '—'}</td>
                      <td>
                        {p.last_pushed_at ? (
                          <span title={p.last_push_status ?? ''}>
                            {p.last_push_status === 'ok' ? '✓' : '!'} {p.last_pushed_at.slice(0, 16)}
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="quick-edit-row">
                        <td colSpan={colSpan}>
                          <QuickEditor product={p} onSave={(patch) => savePatch(p.id, patch)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
            <span className="muted">
              {offset + 1}–{Math.min(offset + items.length, total)} из {total}
            </span>
            <div className="row">
              <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>← Назад</button>
              <button className="btn" disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Вперёд →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
